/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 检测完成结果页面组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-03-03 19:23:44
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { Button, Modal, Empty, Select, message } from 'antd'
import {
  CheckOutlined,
  CheckSquareOutlined,
  CloseOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  LeftOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  useAppDispatch,
  usePayment,
  useVip,
  useVipKind
} from '../../../app/hooks'
import { deletePhoto } from '../../../actions/photo'
import CONFIG from '../../../constants/config'
import useAITaskEvent from '../hooks/useAITaskEvent'
import { useSensorTracking } from '../hooks/useSensorTracking'
import FeatureGateModal from './FeatureGateModal'
import PrivilegeCompareTable, {
  PrivilegeTierKey
} from './PrivilegeCompareTable'
import './DetectionCompleted.scss'

const { Option } = Select

interface DetectionCompletedProps {
  onBackToSelect?: () => void
  photoGroups?: any[]
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
  /** 是否按时间对照片组排序（从配置传入） */
  sortByTime?: boolean
  /** 团队 ID，用于 similar_all */
  groupId?: string
  /** 请求时的时间范围，用于 similar_all */
  fetchTimeRange?: { startTime: number; endTime: number } | null
  /** 每个相似组的图像数量（chunk_size），用于 similar_all */
  chunkSize?: number
  /** 用 similar_all 的 res_list 覆盖指定分组 */
  onReplaceGroupPhotos?: (similarId: string, photoList: any[]) => void
  /** 删除照片成功后，从本地结果中移除对应项 */
  onPhotosDeleted?: (photoSeqs: number[]) => void
}

/**
 * 照片完整信息接口（基于后端返回的数据结构）
 */
interface PhotoItem {
  // 基础信息
  photo_seq: number
  group_id: string
  user_id: string
  photo_time: number // 时间戳（毫秒）
  photo_file: string // 照片文件名
  url?: string // 照片URL（可能从photo_file构建）

  // 地点信息
  photo_location: string // 地点字符串
  photo_lat: number // 纬度
  photo_lng: number // 经度
  photo_fmtaddr: string // 格式化地址

  // 媒体信息
  media_type: number // 媒体类型
  source_type: number // 来源类型
  photo_number: string
  video_info?: string // JSON字符串

  // 水印信息
  group_watermark_id: string
  watermark_base_id: string
  has_watermark: boolean // 是否有水印
  watermark_content?: string // 水印内容
  watermark_info?: string // JSON字符串，水印详细信息

  // 用户评论和元数据（JSON字符串）
  user_comment?: string // JSON字符串
  device_info?: string // JSON字符串
  weather_info?: string // JSON字符串
  location_info?: string // JSON字符串
  extension?: string // JSON字符串，扩展信息

  // 时间戳
  created_at: number // 创建时间戳
  updated_at: string // 更新时间字符串
  deleted_at?: string | null // 删除时间

  // 状态标记
  archive: number
  is_new: boolean
  is_deleted: boolean

  // 前端使用的字段（可能从其他字段解析或mock）
  user_name?: string // 用户昵称（从user_comment或其他字段解析）
  location?: string // 地点（使用photo_location或photo_fmtaddr）
}

interface PhotoGroup {
  similar_id: string
  photo_list: PhotoItem[]
  max_photo_time: number // 最大照片时间戳，用于确定原图
}

const DetectionCompleted: React.FC<DetectionCompletedProps> = ({
  onBackToSelect,
  photoGroups: propsPhotoGroups = [],
  hasMore,
  loadingMore,
  onLoadMore,
  sortByTime = true,
  groupId,
  fetchTimeRange,
  chunkSize = 12, // 默认值，如果未传递则使用 12
  onReplaceGroupPhotos,
  onPhotosDeleted
}) => {
  const dispatch = useAppDispatch()
  const payment = usePayment()
  const { isVip, isSVip, isVVip } = useVip()
  const vipKind = useVipKind()
  const canBatchDelete = isSVip || isVVip

  // 当前会员等级展示文案（用于升级弹窗）
  const currentVipLevelLabel = useMemo(() => {
    if (!isVip || !vipKind) return '非会员'
    const labels: Record<number, string> = {
      [CONFIG.VIP_KIND.VIP]: 'VIP(基础会员)',
      [CONFIG.VIP_KIND.SVIP]: 'SVIP(超级会员)',
      [CONFIG.VIP_KIND.VVIP]: 'VVIP(高级会员)',
      [CONFIG.VIP_KIND.EVIP]: '企业级会员'
    }
    return labels[vipKind] || '会员'
  }, [isVip, vipKind])

  // 升级弹窗：当前等级对应的权益表列（用于高亮「当前」列）
  const upgradeModalCurrentTier = useMemo((): PrivilegeTierKey => {
    if (!isVip || !vipKind) return 'nonVip'
    if (vipKind === CONFIG.VIP_KIND.VIP) return 'vip'
    return 'svipVvip' // SVIP / VVIP / EVIP 均视为同一档
  }, [isVip, vipKind])

  const [selectedPhotos, setSelectedPhotos] = useState<Set<number>>(new Set())
  const [groupSelectedState, setGroupSelectedState] = useState<
    Record<string, boolean>
  >({})
  /** 卡点升级弹窗：符合 PRD 5.3，关闭后保持当前选择状态 */
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false)
  const [upgradeModalMode, setUpgradeModalMode] = useState<'single' | 'batch'>(
    'batch'
  )
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('')
  const [previewPhoto, setPreviewPhoto] = useState<PhotoItem | null>(null)

  // 筛选状态
  const [filterUserName, setFilterUserName] = useState<string>('')
  const [filterLocation, setFilterLocation] = useState<string>('')
  const [filterTime, setFilterTime] = useState<string>('')
  /** 是否有水印：'' 全部，'has' 有水印，'no' 无水印 */
  const [filterWatermark, setFilterWatermark] = useState<string>('')
  const [filterPanelVisible, setFilterPanelVisible] = useState<boolean>(false)
  /** 当前正在加载更多的分组 similar_id */
  const [loadingMoreSimilarId, setLoadingMoreSimilarId] = useState<
    string | null
  >(null)
  /** 每个组的 page_token，用于分页加载 */
  const [groupPageTokens, setGroupPageTokens] = useState<
    Record<string, string>
  >({})
  /** 每个组是否还有更多数据 */
  const [groupHasMore, setGroupHasMore] = useState<Record<string, boolean>>({})

  const { syncPhotoSimilarAll } = useAITaskEvent()
  const { trackClick } = useSensorTracking()

  const openUpgradeModal = useCallback((mode: 'single' | 'batch') => {
    setUpgradeModalMode(mode)
    setUpgradeModalVisible(true)
  }, [])

  const handleUpgradeModalConfirm = useCallback(() => {
    if (upgradeModalMode === 'single') {
      payment.openVipByKind(CONFIG.VIP_KIND.VIP, 'AIDuplicatePhotoSingleDelete')
    } else {
      payment.openVipByKind(CONFIG.VIP_KIND.VVIP, 'AIDuplicatePhotoBatchDelete')
    }
    setUpgradeModalVisible(false)
  }, [upgradeModalMode, payment])

  const handleUpgradeModalCancel = useCallback(() => {
    setUpgradeModalVisible(false)
  }, [])

  // 加载更多：调用 similar_all，追加到当前分组
  const handleLoadMoreGroup = async (similarId: string) => {
    if (!groupId || !fetchTimeRange || !onReplaceGroupPhotos) return
    setLoadingMoreSimilarId(similarId)

    // 获取当前组的 page_token，如果没有则用最后一张照片构建
    let pageToken = groupPageTokens[similarId] || ''
    if (!pageToken) {
      const group = filteredPhotoGroups.find((g) => g.similar_id === similarId)
      if (group && group.photo_list.length > 0) {
        const lastPhoto = group.photo_list[group.photo_list.length - 1]
        // 构建 page_token: "[photo_time, photo_seq]" 格式（JSON 数组字符串）
        pageToken = JSON.stringify([lastPhoto.photo_time, lastPhoto.photo_seq])
      }
    }

    try {
      const res = await syncPhotoSimilarAll({
        group_id: groupId,
        similar_id: similarId,
        chunk_size: chunkSize,
        page_token: pageToken,
        start_time: fetchTimeRange.startTime,
        end_time: fetchTimeRange.endTime
      })
      if (res.code === 200 && res.data?.status === 200 && res.output) {
        const output = res.output
        // @ts-expect-error - similar_all 返回的 res_list 实际包含完整的 PhotoItem 字段
        const newPhotos = (output.res_list || []) as PhotoItem[]
        if (newPhotos.length > 0) {
          // 追加到当前组
          onReplaceGroupPhotos(similarId, newPhotos)
          // 更新 page_token（如果有返回）
          if (output.page_token) {
            setGroupPageTokens((prev) => ({
              ...prev,
              [similarId]: output.page_token
            }))
          } else {
            // 如果没有返回 page_token，用最后一张新照片构建
            const lastNewPhoto = newPhotos[newPhotos.length - 1] as any
            if (
              lastNewPhoto?.photo_time != null &&
              lastNewPhoto?.photo_seq != null
            ) {
              setGroupPageTokens((prev) => ({
                ...prev,
                [similarId]: JSON.stringify([
                  lastNewPhoto.photo_time,
                  lastNewPhoto.photo_seq
                ])
              }))
            }
          }
          // 更新 has_more 状态
          setGroupHasMore((prev) => ({
            ...prev,
            [similarId]: output.has_more === true
          }))
        } else {
          // 没有新照片，标记为没有更多
          setGroupHasMore((prev) => ({
            ...prev,
            [similarId]: false
          }))
          message.info('没有更多照片了')
        }
      } else {
        message.error(res.msg || '加载失败')
      }
    } catch (e) {
      console.error('similar_all 失败:', e)
      message.error('加载失败，请稍后重试')
    } finally {
      setLoadingMoreSimilarId(null)
    }
  }

  // 提取照片信息的辅助函数（userName 接口未返回，为空即可）
  const extractPhotoInfo = (photo: PhotoItem) => {
    let location: string | undefined = undefined

    // 从 watermark_content 中提取地点
    if (photo.watermark_content) {
      try {
        const parts = photo.watermark_content
          .split('蕤')
          .map((p) => p.trim())
          .filter((p) => p)
        if (parts.length >= 4) {
          location = parts[parts.length - 1]?.replace(/,$/, '') || undefined
        }
      } catch (e) {
        console.warn('解析 watermark_content 失败:', e)
      }
    }

    // 使用实际的地点字段（优先级：photo_fmtaddr > photo_location）
    if (!location) {
      location = photo.photo_fmtaddr || photo.photo_location || undefined
    }

    return {
      userName: '',
      location: location ?? '',
      time: photo.photo_time ? formatPhotoTime(photo.photo_time) : ''
    }
  }

  // 构建照片URL的工具函数（原图）
  const buildPhotoUrl = (photo: PhotoItem): string => {
    // 如果已经有 url 字段，直接使用
    if (photo.url) {
      return photo.url
    }

    // 从 photo_file 构建 URL
    if (photo.photo_file) {
      const host = photo.photo_file.includes('tencent-')
        ? CONFIG.tencentPhotoPrefix
        : CONFIG.photoPrefix
      return host + photo.photo_file
    }

    // 如果都没有，返回占位符
    return '/placeholder.jpg'
  }

  // 列表用缩略图 URL：阿里云 x-oss-process 宽 200，腾讯云 imageView2 宽 200
  const LIST_THUMB_WIDTH = 200
  const buildPhotoThumbUrl = (photo: PhotoItem): string => {
    const base = photo.url || buildPhotoUrl(photo)
    if (!base || base === '/placeholder.jpg') return base
    const isTencent =
      photo.photo_file?.includes('tencent-') ?? base.includes('tencent-')
    if (isTencent) {
      const sep = base.includes('?') ? '&' : '?'
      return `${base}${sep}imageView2/2/w/${LIST_THUMB_WIDTH}`
    }
    const sep = base.includes('?') ? '&' : '?'
    return `${base}${sep}x-oss-process=image/resize,w_${LIST_THUMB_WIDTH}`
  }

  // 从实际数据结构中提取用户昵称和地点，并构建URL
  const enrichedPhotoGroups = useMemo(() => {
    const list = (propsPhotoGroups || []) as PhotoGroup[]
    return list.map((group) => ({
      ...group,
      photo_list: group.photo_list.map((photo) => {
        let location: string | undefined = undefined

        // 从 watermark_content 中提取地点
        if (photo.watermark_content) {
          try {
            const parts = photo.watermark_content
              .split('蕤')
              .map((p) => p.trim())
              .filter((p) => p)
            if (parts.length >= 4) {
              location = parts[parts.length - 1]?.replace(/,$/, '') || undefined
            }
          } catch (e) {
            console.warn('解析 watermark_content 失败:', e)
          }
        }

        // 使用实际的地点字段（优先级：photo_fmtaddr > photo_location）
        if (!location) {
          location = photo.photo_fmtaddr || photo.photo_location || undefined
        }

        // 构建照片URL（优先使用已有的 url 字段）
        const photoUrl = photo.url || buildPhotoUrl(photo)

        return {
          ...photo,
          url: photoUrl, // 确保 url 字段存在
          user_name: '', // 接口未返回，为空即可
          location: location ?? '',
          // 确保 photo_time 存在（使用实际的时间戳）
          photo_time: photo.photo_time || 0
        }
      })
    }))
  }, [propsPhotoGroups])

  // 获取所有唯一的用户昵称和地点（用于筛选下拉框）
  const uniqueUserNames = useMemo(() => {
    const names = new Set<string>()
    enrichedPhotoGroups.forEach((group) => {
      group.photo_list.forEach((photo) => {
        if (photo.user_name) {
          names.add(photo.user_name)
        }
      })
    })
    return Array.from(names).sort()
  }, [enrichedPhotoGroups])

  const uniqueLocations = useMemo(() => {
    const locations = new Set<string>()
    enrichedPhotoGroups.forEach((group) => {
      group.photo_list.forEach((photo) => {
        if (photo.location) {
          locations.add(photo.location)
        }
      })
    })
    return Array.from(locations).sort()
  }, [enrichedPhotoGroups])

  // 根据筛选条件过滤照片组；若配置了按时间排序，则按 max_photo_time 降序
  const filteredPhotoGroups = useMemo(() => {
    let list: PhotoGroup[]
    if (!filterUserName && !filterLocation && !filterTime && !filterWatermark) {
      list = enrichedPhotoGroups
    } else {
      list = enrichedPhotoGroups
        .map((group) => {
          const filteredPhotos = group.photo_list.filter((photo) => {
            if (filterUserName && photo.user_name !== filterUserName)
              return false
            if (filterLocation && photo.location !== filterLocation)
              return false
            if (filterTime) {
              const photoDate = dayjs(photo.photo_time).format('YYYY-MM-DD')
              if (photoDate !== filterTime) return false
            }
            if (filterWatermark === 'has' && !photo.has_watermark) return false
            if (filterWatermark === 'no' && photo.has_watermark) return false
            return true
          })
          if (filteredPhotos.length === 0) return null
          return { ...group, photo_list: filteredPhotos }
        })
        .filter((group) => group !== null) as PhotoGroup[]
    }
    if (sortByTime) {
      list = [...list].sort(
        (a, b) => (b.max_photo_time || 0) - (a.max_photo_time || 0)
      )
    }
    return list
  }, [
    enrichedPhotoGroups,
    filterUserName,
    filterLocation,
    filterTime,
    filterWatermark,
    sortByTime
  ])

  // 计算统计数据（用于埋点）- 必须在 filteredPhotoGroups 定义之后
  const getStats = useCallback(() => {
    const totalGroups = filteredPhotoGroups.length
    const totalPhotos = filteredPhotoGroups.reduce(
      (sum, group) => sum + (group.photo_list?.length || 0),
      0
    )
    const selectedCount = selectedPhotos.size
    return {
      number1: totalGroups, // 照片组数
      number2: totalPhotos, // 重复照片总数
      number3: selectedCount, // 选中照片数
      bool1: !!filterUserName, // 是否筛选用户
      bool2: !!filterLocation, // 是否筛选地点
      bool3: !!filterTime, // 是否筛选日期
      bool4: !!filterWatermark // 是否筛选水印
    }
  }, [
    filteredPhotoGroups,
    selectedPhotos.size,
    filterUserName,
    filterLocation,
    filterTime,
    filterWatermark
  ])

  // 进入页面时上报埋点（数据加载完成后）
  useEffect(() => {
    if (filteredPhotoGroups.length > 0) {
      const stats = getStats()
      trackClick({
        actionType: 'enterPage',
        ...stats
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPhotoGroups.length]) // 当有数据时上报一次

  // 使用 ref 存储最新状态，避免 IntersectionObserver 闭包问题
  const groupHasMoreRef = useRef<Record<string, boolean>>({})
  const loadingMoreSimilarIdRef = useRef<string | null>(null)
  const observerMapRef = useRef<Map<string, IntersectionObserver>>(new Map())
  const hasMoreRef = useRef(hasMore)
  const loadingMoreRef = useRef(loadingMore)
  groupHasMoreRef.current = groupHasMore
  loadingMoreSimilarIdRef.current = loadingMoreSimilarId
  hasMoreRef.current = hasMore
  loadingMoreRef.current = loadingMore

  // 上拉加载更多相似组（similar_list）：监听页面底部
  const photoGroupsScrollRef = useRef<HTMLDivElement>(null)
  const loadMoreGroupsSentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!onLoadMore) return
    const sentinel = loadMoreGroupsSentinelRef.current
    const scrollRoot = photoGroupsScrollRef.current
    if (!sentinel || !scrollRoot) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (
          !entry?.isIntersecting ||
          !hasMoreRef.current ||
          loadingMoreRef.current ||
          !onLoadMore
        ) {
          return
        }
        onLoadMore()
      },
      { root: scrollRoot, rootMargin: '120px', threshold: 0 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [onLoadMore])

  // 为每个组的横向滚动容器设置 IntersectionObserver，实现滚动到底部自动加载
  useEffect(() => {
    if (!groupId || !fetchTimeRange || !onReplaceGroupPhotos) return

    // 清理旧的 observers
    observerMapRef.current.forEach((observer) => observer.disconnect())
    observerMapRef.current.clear()

    // 使用 setTimeout 确保 DOM 已更新
    const timer = setTimeout(() => {
      // 查找所有需要监听的哨兵元素
      const sentinels = document.querySelectorAll('.load-more-sentinel')

      sentinels.forEach((sentinel) => {
        const groupIdAttr = sentinel.getAttribute('data-group-id')
        if (!groupIdAttr) return

        // 如果已经有 observer 在监听，跳过
        if (observerMapRef.current.has(groupIdAttr)) return

        // 找到对应的滚动容器（父级的 photos-scroll-container）
        const scrollContainer = sentinel.closest(
          '.photos-scroll-container'
        ) as HTMLElement
        if (!scrollContainer) return

        // 检查哨兵元素是否一开始就在视口内（如果是，说明不需要加载更多）
        const sentinelRect = sentinel.getBoundingClientRect()
        const containerRect = scrollContainer.getBoundingClientRect()
        const isInitiallyVisible =
          sentinelRect.left < containerRect.right &&
          sentinelRect.right > containerRect.left

        // 创建 IntersectionObserver
        const observer = new IntersectionObserver(
          (entries) => {
            const [entry] = entries
            // 只有当元素真正进入视口时才触发
            if (!entry?.isIntersecting) return

            // 检查滚动位置：只有当滚动到接近底部时才触发
            const scrollLeft = scrollContainer.scrollLeft
            const scrollWidth = scrollContainer.scrollWidth
            const clientWidth = scrollContainer.clientWidth
            const scrollRight = scrollLeft + clientWidth

            // 只有当滚动位置接近底部（距离底部小于 100px）时才触发
            const distanceToBottom = scrollWidth - scrollRight
            if (distanceToBottom > 100) return

            // 使用 ref 获取最新状态
            const currentHasMore =
              groupHasMoreRef.current[groupIdAttr] !== false
            const currentLoading =
              loadingMoreSimilarIdRef.current === groupIdAttr

            if (!currentHasMore || currentLoading) return

            // 触发加载更多
            handleLoadMoreGroup(groupIdAttr)
          },
          {
            root: scrollContainer,
            rootMargin: '0px', // 不提前加载，必须真正滚动到底部
            threshold: 0 // 只要进入视口就检测
          }
        )

        // 只有当哨兵元素不在初始视口内时才观察
        if (!isInitiallyVisible) {
          observer.observe(sentinel)
          observerMapRef.current.set(groupIdAttr, observer)
        }
      })
    }, 100) // 增加延迟，确保 DOM 完全渲染

    return () => {
      clearTimeout(timer)
      // 清理所有 observers
      observerMapRef.current.forEach((observer) => observer.disconnect())
      observerMapRef.current.clear()
    }
  }, [
    filteredPhotoGroups,
    groupId,
    fetchTimeRange,
    onReplaceGroupPhotos,
    handleLoadMoreGroup
  ])

  // 初始化每个组的 has_more 状态（默认设为 true，表示可能有更多数据）
  useEffect(() => {
    if (filteredPhotoGroups.length > 0) {
      setGroupHasMore((prev) => {
        const updated = { ...prev }
        filteredPhotoGroups.forEach((group) => {
          if (updated[group.similar_id] === undefined) {
            updated[group.similar_id] = true
          }
        })
        return updated
      })
    }
  }, [filteredPhotoGroups])

  // 全选/取消全选（排除每个组的第一张照片）
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const stats = getStats()
      trackClick({
        actionType: 'selectAll',
        ...stats
      })

      if (!canBatchDelete) {
        openUpgradeModal('batch')
        return
      }

      const allPhotoIds = new Set<number>()
      filteredPhotoGroups.forEach((group) => {
        const photos = group.photo_list || []
        // 排除第一张照片（索引0）
        photos.forEach((photo, index) => {
          if (index > 0) {
            allPhotoIds.add(photo.photo_seq)
          }
        })
      })
      setSelectedPhotos(allPhotoIds)
      const newGroupState: Record<string, boolean> = {}
      filteredPhotoGroups.forEach((group) => {
        const photos = group.photo_list || []
        // 只有当组中有超过1张照片，且除了第一张外其他都被选中时，才标记为全选
        const nonFirstPhotos = photos.filter((_, index) => index > 0)
        const allNonFirstSelected =
          nonFirstPhotos.length > 0 &&
          nonFirstPhotos.every((photo) => allPhotoIds.has(photo.photo_seq))
        newGroupState[group.similar_id] = allNonFirstSelected
      })
      setGroupSelectedState(newGroupState)
    } else {
      setSelectedPhotos(new Set())
      setGroupSelectedState({})

      // 上报埋点
      const stats = getStats()
      trackClick({
        actionType: 'cancelSelectAll',
        ...stats
      })
    }
  }

  // 全选/取消全选某个组（排除第一张照片）
  const handleSelectGroup = (groupId: string, checked: boolean) => {
    const group = filteredPhotoGroups.find((g) => g.similar_id === groupId)
    if (!group) return

    const newSelected = new Set(selectedPhotos)
    const photos = group.photo_list || []
    if (checked) {
      const stats = getStats()
      trackClick({
        actionType: 'selectGroup',
        ...stats
      })

      const selectableCount = photos.reduce((count, _, index) => {
        if (index > 0) return count + 1
        return count
      }, 0)
      if (selectableCount > 1 && !canBatchDelete) {
        openUpgradeModal('batch')
        return
      }

      // 排除第一张照片（索引0）
      photos.forEach((photo, index) => {
        if (index > 0) {
          newSelected.add(photo.photo_seq)
        }
      })
      // 检查是否所有非第一张照片都被选中
      const nonFirstPhotos = photos.filter((_, index) => index > 0)
      const allNonFirstSelected =
        nonFirstPhotos.length > 0 &&
        nonFirstPhotos.every((photo) => newSelected.has(photo.photo_seq))
      setGroupSelectedState({
        ...groupSelectedState,
        [groupId]: allNonFirstSelected
      })
    } else {
      photos.forEach((photo) => {
        newSelected.delete(photo.photo_seq)
      })
      setGroupSelectedState({ ...groupSelectedState, [groupId]: false })

      // 上报埋点
      const stats = getStats()
      trackClick({
        actionType: 'cancelSelectGroup',
        ...stats
      })
    }
    setSelectedPhotos(newSelected)
  }

  // 选择/取消选择单个照片
  const handleSelectPhoto = (photoId: number, checked: boolean) => {
    const newSelected = new Set(selectedPhotos)
    if (checked) {
      newSelected.add(photoId)
    } else {
      newSelected.delete(photoId)
    }
    setSelectedPhotos(newSelected)

    // 更新组的选中状态（排除第一张照片，只检查非第一张照片是否都被选中）
    filteredPhotoGroups.forEach((group) => {
      const photos = group.photo_list || []
      // 只检查非第一张照片是否都被选中
      const nonFirstPhotos = photos.filter((_, index) => index > 0)
      const allNonFirstSelected =
        nonFirstPhotos.length > 0 &&
        nonFirstPhotos.every((photo) => newSelected.has(photo.photo_seq))
      setGroupSelectedState({
        ...groupSelectedState,
        [group.similar_id]: allNonFirstSelected
      })
    })
  }

  // 删除所选照片
  const handleDeleteSelected = () => {
    if (selectedPhotos.size === 0) {
      return
    }

    // 上报埋点 - 点击删除按钮
    const stats = getStats()
    trackClick({
      actionType: 'delete',
      ...stats
    })

    if (selectedPhotos.size > 1 && !canBatchDelete) {
      openUpgradeModal('batch')
      return
    }
    if (selectedPhotos.size === 1 && !isVip) {
      openUpgradeModal('single')
      return
    }

    Modal.confirm({
      title: '是否删除所选的照片？',
      content: '删除以后照片将进入回收站。不占用存储空间',
      okText: '确认删除',
      cancelText: '取消',
      okType: 'primary',
      onOk: () => {
        // 上报埋点 - 确认删除
        const confirmStats = getStats()
        trackClick({
          actionType: 'confirmDelete',
          ...confirmStats
        })

        if (!groupId) {
          message.error('未获取到团队ID，请刷新重试')
          return Promise.resolve()
        }
        const photoList = Array.from(selectedPhotos)
        return dispatch(
          deletePhoto({
            group_id: groupId,
            photo_list: photoList
          })
        )
          .then(() => {
            if (onPhotosDeleted) onPhotosDeleted(photoList)
            setSelectedPhotos(new Set())
            setGroupSelectedState({})
          })
          .catch((err) => {
            // exceptionHelper 已经处理了大部分错误（如 -10 不是管理员、-12 无权限等）
            // 并会显示相应的 Modal 提示
            // 由于 deletePhoto 的 catch 返回 reject(null)，错误信息可能丢失
            // 但如果 err 存在且有错误信息，我们可以补充处理一些特殊情况
            if (err && err.data) {
              const status = err.data.status
              const msg = err.data.msg || err.msg

              // exceptionHelper 已处理的错误状态码：
              // -10: 不是管理员
              // -12: 无查看权限
              // -3: 已退出或被移出团队
              // -1: 登录状态失效
              // -40: 删除失败
              // -205: 仅能删除自己的照片
              // -206: 照片超过10000张 0414版本修改上限为10000张，对应文案也得修改
              // -56: 无操作权限

              // 如果 exceptionHelper 没有处理该错误状态码，且有错误消息，则显示
              if (
                msg &&
                ![-10, -12, -56, -3, -1, -40, -205, -206].includes(status)
              ) {
                message.error(msg)
              }
            }
            // 如果 err 为 null，说明 exceptionHelper 已经处理了错误并显示了相应的 Modal
          })
      },
      onCancel: () => {
        // 上报埋点 - 取消删除
        const stats = getStats()
        trackClick({
          actionType: 'cancelDelete',
          ...stats
        })
      }
    })
  }

  // 预览图片
  const handlePreviewPhoto = (photo: PhotoItem) => {
    if (!photo) return
    const imageUrl = photo.url || buildPhotoUrl(photo) || '/placeholder.jpg'
    setPreviewImageUrl(imageUrl)
    setPreviewPhoto(photo)
    setPreviewVisible(true)

    // 上报埋点
    const stats = getStats()
    trackClick({
      actionType: 'preview',
      ...stats
    })
  }

  // 关闭预览
  const handleClosePreview = () => {
    setPreviewVisible(false)
    setPreviewImageUrl('')
    setPreviewPhoto(null)

    // 上报埋点
    const stats = getStats()
    trackClick({
      actionType: 'cancelPreview',
      ...stats
    })
  }

  // 全选状态：排除每个组的第一张照片
  const isAllSelected =
    filteredPhotoGroups.length > 0 &&
    filteredPhotoGroups.every((group) => {
      const photos = group.photo_list || []
      // 只检查非第一张照片是否都被选中
      const nonFirstPhotos = photos.filter((_, index) => index > 0)
      return (
        nonFirstPhotos.length > 0 &&
        nonFirstPhotos.every((photo) => selectedPhotos.has(photo.photo_seq))
      )
    })

  // 格式化时间显示
  const formatPhotoTime = (timestamp: number) => {
    return dayjs(timestamp).format('YYYY-MM-DD')
  }

  // 获取所有唯一的时间（日期）用于筛选
  const uniqueDates = useMemo(() => {
    const dates = new Set<string>()
    enrichedPhotoGroups.forEach((group) => {
      group.photo_list.forEach((photo) => {
        const date = dayjs(photo.photo_time).format('YYYY-MM-DD')
        dates.add(date)
      })
    })
    return Array.from(dates).sort().reverse() // 最新的在前
  }, [enrichedPhotoGroups])

  // 获取选中项的缩略图（最多3张）
  const getSelectedThumbnails = () => {
    const thumbnails: string[] = []
    filteredPhotoGroups.forEach((group) => {
      const photos = group.photo_list || []
      photos.forEach((photo) => {
        if (selectedPhotos.has(photo.photo_seq) && thumbnails.length < 3) {
          const photoUrl = buildPhotoThumbUrl(photo)
          if (photoUrl) {
            thumbnails.push(photoUrl)
          }
        }
      })
    })
    return thumbnails
  }

  // 重置筛选
  const handleResetFilter = () => {
    setFilterUserName('')
    setFilterLocation('')
    setFilterTime('')
    setFilterWatermark('')
  }

  return (
    <div className="detection-completed">
      {/* 顶部操作栏和标题 */}
      <div className="top-header">
        <div className="header-left">
          <div
            className="back-btn"
            onClick={() => {
              const stats = getStats()
              trackClick({
                actionType: 'back',
                ...stats
              })
              if (onBackToSelect) onBackToSelect()
            }}
          >
            <LeftOutlined className="back-icon" />
            <span className="back-text">返回筛选时间</span>
          </div>
          <div className="header-info">
            <h1 className="result-title">相似照片检测结果</h1>
            <p className="result-description">
              AI 在 {enrichedPhotoGroups.length} 组照片中检测到{' '}
              {enrichedPhotoGroups.reduce(
                (sum, group) => sum + (group.photo_list?.length || 0),
                0
              )}{' '}
              张重复项。
            </p>
          </div>
        </div>
        {enrichedPhotoGroups.length > 0 && (
          <div className="top-actions">
            <Button
              className={`filter-btn ${filterPanelVisible ? 'active' : ''} ${
                filterUserName ||
                filterLocation ||
                filterTime ||
                filterWatermark
                  ? 'has-filter'
                  : ''
              }`}
              icon={<FilterOutlined />}
              onClick={() => {
                const stats = getStats()
                trackClick({
                  actionType: 'filter',
                  ...stats
                })
                setFilterPanelVisible(!filterPanelVisible)
              }}
            >
              筛选
              {(filterUserName ||
                filterLocation ||
                filterTime ||
                filterWatermark) && (
                <span className="filter-badge">
                  {
                    [
                      filterUserName,
                      filterLocation,
                      filterTime,
                      filterWatermark
                    ].filter(Boolean).length
                  }
                </span>
              )}
            </Button>
            <Button
              className="select-all-btn"
              icon={<CheckSquareOutlined />}
              onClick={() => handleSelectAll(!isAllSelected)}
            >
              全选
            </Button>
          </div>
        )}
      </div>

      {/* 筛选面板（可展开/收起） */}
      {enrichedPhotoGroups.length > 0 && filterPanelVisible && (
        <div className="filter-panel">
          <div className="filter-content">
            {/* 昵称筛选（接口未返回用户昵称，暂不展示）
            <div className="filter-item">
              <label className="filter-label">用户昵称</label>
              <Select
                className="filter-select"
                placeholder="全部"
                value={filterUserName || undefined}
                onChange={(value) => setFilterUserName(value || '')}
                allowClear
              >
                {uniqueUserNames.map((name) => (
                  <Option key={name} value={name}>
                    {name}
                  </Option>
                ))}
              </Select>
            </div>
            */}
            <div className="filter-item">
              <label className="filter-label">地点</label>
              <Select
                className="filter-select"
                placeholder="全部"
                value={filterLocation || undefined}
                onChange={(value) => setFilterLocation(value || '')}
                allowClear
              >
                {uniqueLocations.map((location) => (
                  <Option key={location} value={location}>
                    {location}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="filter-item">
              <label className="filter-label">时间</label>
              <Select
                className="filter-select"
                placeholder="全部"
                value={filterTime || undefined}
                onChange={(value) => setFilterTime(value || '')}
                allowClear
              >
                {uniqueDates.map((date) => (
                  <Option key={date} value={date}>
                    {dayjs(date).format('YYYY年M月D日')}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="filter-item">
              <label className="filter-label">是否有水印</label>
              <Select
                className="filter-select"
                placeholder="全部"
                value={filterWatermark || undefined}
                onChange={(value) => setFilterWatermark(value || '')}
                allowClear
              >
                <Option value="has">是</Option>
                <Option value="no">否</Option>
              </Select>
            </div>
            {(filterUserName ||
              filterLocation ||
              filterTime ||
              filterWatermark) && (
              <Button className="reset-filter-btn" onClick={handleResetFilter}>
                重置
              </Button>
            )}
          </div>
        </div>
      )}

      {/* 照片组列表（滚动容器，作为 IntersectionObserver 的 root） */}
      <div ref={photoGroupsScrollRef} className="photo-groups">
        {filteredPhotoGroups.length === 0 ? (
          <div className="empty-state-wrapper">
            <Empty
              description="暂无相似照片"
              className="empty-state"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
            <Button type="primary" onClick={onBackToSelect}>
              返回
            </Button>
          </div>
        ) : (
          filteredPhotoGroups.map((group, groupIndex) => {
            const groupId = group.similar_id
            const groupTitle = `相似照片组 #${groupIndex + 1}`
            const photos = group.photo_list || []
            const photoCount = photos.length
            // 组选中状态：排除第一张照片
            const nonFirstPhotos = photos.filter((_, index) => index > 0)
            const isGroupSelected =
              nonFirstPhotos.length > 0 &&
              nonFirstPhotos.every((p) => selectedPhotos.has(p.photo_seq))

            return (
              <div key={groupId} className="photo-group">
                <div className="group-header">
                  <div className="group-header-left">
                    <h3 className="group-title">{groupTitle}</h3>
                    <span className="group-info">{photoCount} 张照片</span>
                  </div>
                  <button
                    className="select-best-btn"
                    onClick={() => handleSelectGroup(groupId, !isGroupSelected)}
                  >
                    {isGroupSelected ? '取消该组' : '选中该组'}
                  </button>
                </div>
                <div
                  className="photos-scroll-container"
                  data-group-id={groupId}
                >
                  <div className="photos-grid">
                    {photos.map((photo, photoIndex) => {
                      const photoId = photo.photo_seq
                      const imageUrl =
                        buildPhotoThumbUrl(photo) || '/placeholder.jpg'
                      const isSelected = selectedPhotos.has(photoId)
                      // 通过数组确定展示哪些标签，支持多标签、一行最多 3 个自动换行
                      const hasWatermark = photo.has_watermark
                      const now = Date.now()
                      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
                      const isRecent7Days =
                        photo.photo_time &&
                        photo.photo_time >= now - sevenDaysMs
                      const tagList: { id: string; text: string }[] = []
                      if (photoIndex === 0)
                        tagList.push({ id: 'latest', text: '最新' })
                      if (isRecent7Days)
                        tagList.push({ id: 'recent7', text: '近7天' })
                      if (!hasWatermark)
                        tagList.push({ id: 'noWatermark', text: '不含水印' })
                      // 标签色板（按 id 取模得到稳定颜色）
                      const tagColors = [
                        '#10b981',
                        '#6366f1',
                        '#f59e0b',
                        '#ec4899',
                        '#64748b',
                        '#0ea5e9'
                      ]
                      const getTagColor = (id: string, index: number) =>
                        tagColors[(photoId + index) % tagColors.length]

                      // 检查是否需要在当前位置添加分割点（配置数量的倍数）
                      const shouldShowDivider =
                        photoIndex > 0 && photoIndex % chunkSize === 0

                      return (
                        <React.Fragment key={`fragment-${photoId}`}>
                          {/* 竖向分割点：在配置数量倍数处显示 */}
                          {shouldShowDivider && (
                            <div
                              key={`divider-${groupId}-${photoIndex}`}
                              className="photo-divider"
                              title={`已查看 ${photoIndex} 张照片`}
                            >
                              <span className="divider-text">{`第${
                                Math.floor(photoIndex / chunkSize) + 1
                              }页`}</span>
                            </div>
                          )}
                          <div
                            key={photoId}
                            className={`photo-item ${
                              isSelected ? 'selected' : ''
                            }`}
                          >
                            {tagList.length > 0 && (
                              <div className="photo-badges">
                                {tagList.slice(0, 6).map((tag, idx) => (
                                  <span
                                    key={tag.id}
                                    className="photo-badge"
                                    style={{
                                      backgroundColor: getTagColor(tag.id, idx)
                                    }}
                                  >
                                    {tag.text}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div
                              className="photo-image-wrapper"
                              onClick={() => handlePreviewPhoto(photo)}
                            >
                              <img
                                src={imageUrl}
                                alt=""
                                className="photo-image"
                              />
                              <div
                                className={`photo-checkbox-wrapper ${
                                  isSelected ? 'checked' : ''
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation() // 阻止触发预览
                                  handleSelectPhoto(photoId, !isSelected)
                                }}
                              >
                                <div className="photo-checkbox-circle">
                                  {isSelected && (
                                    <CheckOutlined className="check-icon" />
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="photo-info">
                              <div className="photo-meta">
                                <div className="photo-meta-row">
                                  {photo.user_name ? (
                                    <span className="photo-user-name">
                                      {photo.user_name}
                                    </span>
                                  ) : null}
                                  {photo.photo_time ? (
                                    <span className="photo-time">
                                      {formatPhotoTime(photo.photo_time)}
                                    </span>
                                  ) : null}
                                </div>
                                {photo.location ? (
                                  <div className="photo-location-row">
                                    <EnvironmentOutlined className="location-icon" />
                                    <span className="photo-location">
                                      {photo.location}
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      )
                    })}
                    {/* 滚动到底部自动加载的哨兵元素 */}
                    {groupId &&
                      fetchTimeRange &&
                      onReplaceGroupPhotos &&
                      groupHasMore[groupId] !== false && (
                        <div
                          className="load-more-sentinel"
                          data-group-id={groupId}
                        />
                      )}
                  </div>
                </div>
              </div>
            )
          })
        )}

        {/* 上拉加载更多相似组（similar_list）的哨兵元素 */}
        {hasMore && onLoadMore && (
          <div
            ref={loadMoreGroupsSentinelRef}
            className="load-more-groups-sentinel"
            aria-hidden
          >
            {loadingMore && (
              <span className="load-more-loading">加载中...</span>
            )}
          </div>
        )}
      </div>

      {/* 底部固定操作栏 */}
      {selectedPhotos.size > 0 && (
        <div className="bottom-action-bar">
          <div className="action-bar-left">
            <div className="selected-info">
              <span className="selected-count">
                已选中 {selectedPhotos.size} 项
              </span>
            </div>
            <div className="divider" />
            <div className="thumbnails">
              {getSelectedThumbnails().map((url, index) => (
                <div key={index} className="thumbnail">
                  <img src={url} alt="" />
                </div>
              ))}
              {selectedPhotos.size > 3 && (
                <div className="thumbnail more">+{selectedPhotos.size - 3}</div>
              )}
            </div>
          </div>
          <div className="action-bar-right">
            <Button
              className="cancel-select-btn"
              onClick={() => handleSelectAll(false)}
            >
              取消全选
            </Button>
            <Button
              className="delete-btn"
              icon={<DeleteOutlined />}
              onClick={handleDeleteSelected}
            >
              删除选中项
            </Button>
          </div>
        </div>
      )}

      {/* 卡点升级弹窗：先提示当前等级，再展示权益对比表 */}
      <FeatureGateModal
        visible={upgradeModalVisible}
        onCancel={handleUpgradeModalCancel}
        title={
          upgradeModalMode === 'batch'
            ? '解锁批量删除能力'
            : '开通会员后可删除照片'
        }
        description={`您当前为【${currentVipLevelLabel}】。会员等级权益对比如下：`}
        width={480}
        maskClosable
        primaryButton={{ text: '立即升级', onClick: handleUpgradeModalConfirm }}
        secondaryButton={{
          text: '暂不升级',
          onClick: handleUpgradeModalCancel
        }}
      >
        <PrivilegeCompareTable currentTier={upgradeModalCurrentTier} />
      </FeatureGateModal>

      {/* 图片预览 */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={handleClosePreview}
        closable
        closeIcon={
          <CloseOutlined style={{ color: 'white', fontSize: '24px' }} />
        }
        centered
        styles={{
          body: {
            padding: 0,
            textAlign: 'center',
            background: 'black',
            fontSize: '0'
          }
        }}
        width="100%"
        style={{ top: 0 }}
        mask={{ closable: true }}
        wrapClassName="photo-preview-modal"
      >
        <div className="photo-preview-container">
          <div className="photo-preview-content">
            <div className="photo-preview-image-wrapper">
              <img
                onClick={handleClosePreview}
                src={previewImageUrl}
                alt="预览图片"
                className="photo-preview-image"
              />
            </div>
            {previewPhoto &&
              (() => {
                const photoInfo = extractPhotoInfo(previewPhoto)
                return (
                  <div className="photo-preview-sidebar">
                    <div className="photo-preview-info">
                      <div className="photo-preview-meta-row">
                        {photoInfo.userName ? (
                          <span className="photo-preview-user-name">
                            {photoInfo.userName}
                          </span>
                        ) : null}
                        {photoInfo.time ? (
                          <span className="photo-preview-time">
                            {photoInfo.time}
                          </span>
                        ) : null}
                      </div>
                      {photoInfo.location ? (
                        <div className="photo-preview-location-row">
                          <EnvironmentOutlined className="location-icon" />
                          <span className="photo-preview-location">
                            {photoInfo.location}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })()}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default DetectionCompleted
